// Page publique de l'outil gratuit d'estimation : « Combien vous coûterait une cyberattaque ? » (#/estimation).
// Sans compte, sans dépôt de document, sans modèle d'IA : quinze questions fermées, le calcul est fait par le moteur
// déterministe d'Ekera côté serveur. Les réponses restent en mémoire de la page ; rien n'est écrit dans l'adresse,
// un stockage ou la console. Le courriel n'est demandé qu'à la fin, pour recevoir le résultat, avec un consentement
// explicite ; une demande de contact n'est transmise à Ekera qu'après le clic sur le lien reçu à cette adresse
// (PageConfirmationContact, #/estimation/contact). La page renvoie à la politique de confidentialité (lien fourni par
// le serveur, réglage EKERA_ESTIMATION_LIEN_CONFIDENTIALITE).
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { eurosLong, fourchette } from '../domaine/format';
import {
  ClientEstimation,
  LIBELLE_ETAT_NIS2,
  jetonDuFragment,
  messageErreur,
  nombreRepondues,
  saisieComplete,
  versReponses,
  type Questionnaire,
  type ResultatEstimation,
  type Saisie,
} from '../domaine/estimation';
import '../styles/estimation.css';

const TON_ETAT: Record<string, string> = { tenue: 'ok', partielle: 'attention', en_ecart: 'grave' };

export function PageEstimation({ client: fourni }: { client?: ClientEstimation }) {
  const client = useMemo(() => fourni ?? new ClientEstimation(), [fourni]);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<Saisie>({});
  const [etat, setEtat] = useState<'saisie' | 'calcul' | 'resultat'>('saisie');
  const [erreur, setErreur] = useState<string | null>(null);
  const [resultat, setResultat] = useState<ResultatEstimation | null>(null);
  const titreResultat = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = 'Combien vous coûterait une cyberattaque ? — Ekera';
    let actif = true;
    client
      .questionnaire()
      .then((q) => actif && setQuestionnaire(q))
      .catch((e: unknown) => actif && setErreurChargement(messageErreur(e)));
    return () => {
      actif = false;
    };
  }, [client]);

  useEffect(() => {
    if (etat === 'resultat') titreResultat.current?.focus();
  }, [etat]);

  if (erreurChargement)
    return (
      <Cadre>
        <p role="alert" className="estim-alerte">
          {erreurChargement}
        </p>
      </Cadre>
    );
  if (!questionnaire)
    return (
      <Cadre>
        <p aria-busy="true" className="muted">
          Chargement du questionnaire…
        </p>
      </Cadre>
    );

  const q = questionnaire;
  const complete = saisieComplete(q, saisie);
  const faites = nombreRepondues(q, saisie);

  async function calculer(ev: FormEvent) {
    ev.preventDefault();
    if (!complete) return;
    setErreur(null);
    setEtat('calcul');
    try {
      setResultat(await client.estimer(versReponses(q, saisie)));
      setEtat('resultat');
    } catch (e) {
      setErreur(messageErreur(e));
      setEtat('saisie');
    }
  }

  return (
    <Cadre confidentialite={q.lien_confidentialite}>
      <section className="estim-tete" aria-labelledby="titre-estimation">
        <p className="estim-surtitre">Outil gratuit · sans compte · sans document</p>
        <h1 id="titre-estimation">Combien vous coûterait une cyberattaque ?</h1>
        <p className="estim-chapo">
          Réponse en 15 minutes. Quinze questions fermées sur votre entreprise ; le moteur de calcul d’Ekera en tire les
          pertes potentielles estimées par an, le pire scénario, un indicateur de progression NIS2 et les trois mesures
          qui réduiraient le plus le risque.
        </p>
        <ul className="estim-promesses">
          <li>Aucun document à déposer, aucun compte à créer.</li>
          <li>Aucun modèle d’IA : un calcul déterministe, les mêmes réponses donnent le même résultat.</li>
          <li>Vos réponses ne sont pas gardées : seul un compteur anonyme du nombre d’estimations par jour est tenu.</li>
        </ul>
      </section>

      {etat !== 'resultat' && (
        <form className="estim-form" onSubmit={(e) => void calculer(e)} noValidate>
          {q.questions.map((x, i) => (
            <fieldset key={x.id} className="estim-question">
              <legend>
                <span className="estim-num" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="estim-texte">{x.texte}</span>
              </legend>
              <p className="estim-aide" id={`aide-${x.id}`}>
                {x.aide}
              </p>
              <div className="estim-options">
                {x.options.map((o) => {
                  const id = `${x.id}-${o.valeur}`;
                  const v = saisie[x.id];
                  const coche = x.multiple ? Array.isArray(v) && v.includes(o.valeur) : v === o.valeur;
                  return (
                    <label key={o.valeur} htmlFor={id} className={`estim-option${coche ? ' coche' : ''}`}>
                      <input
                        id={id}
                        type={x.multiple ? 'checkbox' : 'radio'}
                        name={x.id}
                        value={o.valeur}
                        checked={coche}
                        aria-describedby={`aide-${x.id}`}
                        onChange={(e) =>
                          setSaisie((s) => {
                            if (!x.multiple) return { ...s, [x.id]: o.valeur };
                            const avant = Array.isArray(s[x.id]) ? (s[x.id] as string[]) : [];
                            return {
                              ...s,
                              [x.id]: e.target.checked ? [...avant, o.valeur] : avant.filter((y) => y !== o.valeur),
                            };
                          })
                        }
                      />
                      <span>{o.libelle}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
          <div className="estim-envoi">
            <p className="muted" aria-live="polite">
              {faites} question{faites > 1 ? 's' : ''} sur {q.questions.length}
              {complete ? ' : prêt pour le calcul.' : ' (les questions à choix multiples peuvent rester vides).'}
            </p>
            {erreur && (
              <p role="alert" className="estim-alerte">
                {erreur}
              </p>
            )}
            <button type="submit" className="btn principal estim-gros" disabled={!complete || etat === 'calcul'}>
              {etat === 'calcul' ? 'Calcul en cours…' : 'Calculer l’estimation'}
            </button>
            {etat === 'calcul' && (
              <p className="muted" role="status">
                Vérification anti-abus dans votre navigateur, puis calcul par le moteur d’Ekera : quelques secondes.
              </p>
            )}
          </div>
        </form>
      )}

      {etat === 'resultat' && resultat && (
        <Resultat
          r={resultat}
          q={q}
          titre={titreResultat}
          surRecommencer={() => {
            setResultat(null);
            setEtat('saisie');
          }}
          envoyer={(courriel, recontact) => client.envoyer(versReponses(q, saisie), courriel, recontact)}
        />
      )}
    </Cadre>
  );
}

function Cadre({ children, confidentialite }: { children: React.ReactNode; confidentialite?: string }) {
  return (
    <div className="estim">
      <header className="estim-bandeau">
        <span className="estim-marque">Ekera</span>
        <span className="muted">Vos réponses ne sont pas gardées</span>
      </header>
      <main id="contenu" className="estim-page">
        {children}
      </main>
      <footer className="estim-pied muted">
        Ekera · calcul déterministe, sans modèle d’IA · hébergé en France
        {confidentialite && (
          <>
            {' · '}
            <LienConfidentialite href={confidentialite} />
          </>
        )}
      </footer>
    </div>
  );
}

/** Lien vers la politique de confidentialité : adresse https:// fournie par le serveur (réglage), jamais construite ici.
 * La page visée est un emplacement à rédiger, à relire par l’avocat avant l’ouverture au public. */
function LienConfidentialite({ href }: { href: string }) {
  if (!href.startsWith('https://')) return null;
  return (
    <a href={href} rel="noopener noreferrer">
      Comment Ekera traite vos données
    </a>
  );
}

function Resultat({
  r,
  q,
  titre,
  surRecommencer,
  envoyer,
}: {
  r: ResultatEstimation;
  q: Questionnaire;
  titre: React.RefObject<HTMLHeadingElement | null>;
  surRecommencer: () => void;
  envoyer: (courriel: string, recontact: boolean) => Promise<{ recontact: boolean }>;
}) {
  const p = r.pire_scenario;
  return (
    <section className="estim-resultat" aria-labelledby="titre-resultat">
      <h2 id="titre-resultat" tabIndex={-1} ref={titre}>
        Votre estimation
      </h2>
      <p className="estim-declaratif">Estimation à partir de réponses déclaratives, sans lecture de vos documents.</p>

      <div className="estim-chiffres">
        <article className="estim-chiffre principal">
          <h3>Pertes potentielles estimées par an</h3>
          <p className="estim-valeur num">{fourchette(r.pertes.bas, r.pertes.haut)}</p>
          <p className="estim-detail">
            De {eurosLong(r.pertes.bas)} à {eurosLong(r.pertes.haut)} par an : pertes potentielles estimées,
            conditionnelles aux hypothèses retenues.
          </p>
        </article>
        {p && (
          <article className="estim-chiffre">
            <h3>Pire scénario</h3>
            <p className="estim-valeur num">{fourchette(p.bas, p.haut)}</p>
            <p className="estim-detail">
              Une attaque qui entre par « {p.porte_entree} »
              {p.restaurable ? ', avec une sauvegarde restaurable.' : ', sans sauvegarde restaurable.'} Pertes
              potentielles estimées si elle aboutit.
            </p>
          </article>
        )}
        <article className="estim-chiffre">
          <h3>Indicateur de progression NIS2</h3>
          <p className="estim-valeur num">
            {r.nis2.indicateur.toLocaleString('fr-FR')} <span className="estim-sur">/ {r.nis2.sur}</span>
          </p>
          <p className="estim-detail">
            Indicateur de progression sur les mesures évaluées, pas une certification de conformité.{' '}
            {r.nis2.tenues} mesure{r.nis2.tenues > 1 ? 's' : ''} tenue{r.nis2.tenues > 1 ? 's' : ''} sur{' '}
            {r.nis2.mesures.length}.
          </p>
        </article>
      </div>

      <details className="estim-nis2">
        <summary>Les dix mesures de l’article 21 évaluées</summary>
        <ul>
          {r.nis2.mesures.map((m) => (
            <li key={m.mesure}>
              <span className={`etiquette ${TON_ETAT[m.etat] ?? 'neutre'}`}>{LIBELLE_ETAT_NIS2[m.etat] ?? m.etat}</span>{' '}
              {m.libelle}
            </li>
          ))}
        </ul>
      </details>

      <h2 className="estim-h2">Les trois mesures qui réduiraient le plus le risque</h2>
      <p className="muted">Scénarios d’investissement proposés, à valider par l’entreprise.</p>
      <ol className="estim-mesures">
        {r.mesures.map((m) => (
          <li key={m.libelle}>
            <h3>{m.libelle}</h3>
            {m.detail && <p className="muted">{m.detail}</p>}
            <p>
              <span className="estim-cout" aria-label={`Ordre de coût : ${m.ordre_de_cout} sur trois ans`}>
                {'€'.repeat(m.niveau_de_cout)}
                <span className="estim-cout-vide" aria-hidden="true">
                  {'€'.repeat(3 - m.niveau_de_cout)}
                </span>
              </span>{' '}
              Ordre de coût : {m.ordre_de_cout} sur trois ans.
            </p>
            <p>
              Pertes potentielles estimées réduites de {fourchette(m.reduction.bas, m.reduction.haut)} par an.
              {m.ensemble_avec && ` À mener avec « ${m.ensemble_avec} » : l’une ne vaut pas sans l’autre.`}
            </p>
          </li>
        ))}
      </ol>

      {r.points_a_confirmer.length > 0 && (
        <section className="estim-points" aria-labelledby="titre-points">
          <h3 id="titre-points">Points à confirmer avant de prendre une décision</h3>
          <ul>
            {r.points_a_confirmer.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </section>
      )}

      <details className="estim-hypotheses">
        <summary>Hypothèses retenues</summary>
        <ul>
          {r.hypotheses.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </details>

      <section className="estim-mentions" aria-label="Ce que sont ces chiffres">
        <ul>
          {r.mentions.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </section>

      <div className="estim-action">
        <div>
          <h2 className="estim-h2">Allez plus loin que les réponses déclaratives</h2>
          <p>
            Le diagnostic complet lit vos documents et vos exports, dessine la carte réelle de votre système
            d’information et chiffre chaque scénario avec vos comptes.
          </p>
        </div>
        <a className="btn principal estim-gros" href={q.lien_inscription} rel="noopener">
          Obtenir le diagnostic complet
        </a>
      </div>

      {q.courriel_disponible && (
        <EnvoiCourriel
          recontactDisponible={q.recontact_disponible}
          confidentialite={q.lien_confidentialite}
          envoyer={envoyer}
        />
      )}

      <p>
        <button type="button" className="lien-btn" onClick={surRecommencer}>
          Modifier mes réponses
        </button>
      </p>
    </section>
  );
}

function EnvoiCourriel({
  recontactDisponible,
  confidentialite,
  envoyer,
}: {
  recontactDisponible: boolean;
  confidentialite: string;
  envoyer: (courriel: string, recontact: boolean) => Promise<{ recontact: boolean }>;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [courriel, setCourriel] = useState('');
  const [consentement, setConsentement] = useState(false);
  const [recontact, setRecontact] = useState(false);
  const [etat, setEtat] = useState<'saisie' | 'envoi' | 'envoye'>('saisie');
  const [erreur, setErreur] = useState<string | null>(null);
  const valide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(courriel.trim()) && consentement;

  if (!ouvert)
    return (
      <p>
        <button type="button" className="btn" onClick={() => setOuvert(true)}>
          Recevoir ce résultat par courriel
        </button>
      </p>
    );
  if (etat === 'envoye')
    return (
      <p role="status" className="estim-ok">
        {recontact
          ? 'Envoi demandé : le résultat part vers votre adresse dans quelques instants, avec un lien pour confirmer ' +
            'votre demande de contact (valable 24 heures, une seule fois). Sans cette confirmation, rien n’est ' +
            'transmis à Ekera et votre adresse n’est pas gardée.'
          : 'Envoi demandé : le résultat part vers votre adresse dans quelques instants. Ekera ne garde pas votre ' +
            'adresse.'}
      </p>
    );

  async function soumettre(ev: FormEvent) {
    ev.preventDefault();
    if (!valide) return;
    setErreur(null);
    setEtat('envoi');
    try {
      const r = await envoyer(courriel.trim(), recontactDisponible && recontact);
      setRecontact(r.recontact);
      setCourriel('');
      setEtat('envoye');
    } catch (e) {
      setErreur(messageErreur(e));
      setEtat('saisie');
    }
  }

  return (
    <form className="estim-courriel" onSubmit={(e) => void soumettre(e)} noValidate>
      <h2 className="estim-h2">Recevoir ce résultat par courriel</h2>
      <label htmlFor="estim-courriel">Votre courriel professionnel</label>
      <input
        id="estim-courriel"
        type="email"
        inputMode="email"
        autoComplete="email"
        maxLength={254}
        value={courriel}
        onChange={(e) => setCourriel(e.target.value)}
        aria-describedby="estim-courriel-aide"
      />
      <p id="estim-courriel-aide" className="muted">
        Les messageries grand public et jetables sont refusées. L’adresse sert à cet envoi, puis elle est oubliée, sauf
        si vous demandez à être recontacté et le confirmez par le lien reçu.
      </p>
      <label className="estim-case">
        <input type="checkbox" checked={consentement} onChange={(e) => setConsentement(e.target.checked)} />
        <span>J’accepte de recevoir ce résultat à cette adresse (obligatoire).</span>
      </label>
      {recontactDisponible && (
        <label className="estim-case">
          <input type="checkbox" checked={recontact} onChange={(e) => setRecontact(e.target.checked)} />
          <span>
            J’accepte qu’Ekera me recontacte à cette adresse au sujet du diagnostic complet (facultatif). Un lien de
            confirmation part avec le résultat : rien n’est transmis à Ekera avant que vous l’ouvriez.
          </span>
        </label>
      )}
      <p className="muted">
        <LienConfidentialite href={confidentialite} />
      </p>
      {erreur && (
        <p role="alert" className="estim-alerte">
          {erreur}
        </p>
      )}
      <button type="submit" className="btn principal" disabled={!valide || etat === 'envoi'}>
        {etat === 'envoi' ? 'Envoi…' : 'Envoyer le résultat'}
      </button>
    </form>
  );
}

/** Confirmation d'une demande de contact (#/estimation/contact?jeton=…), ouverte depuis le lien reçu par courriel.
 * Le jeton est lu dans le fragment (jamais envoyé au serveur dans une adresse), retiré aussitôt de la barre d'adresse
 * et de l'historique, puis posté **seulement sur un clic** : un logiciel qui ouvre les liens d'un courriel pour les
 * analyser ne confirme rien. */
export function PageConfirmationContact({ client: fourni }: { client?: ClientEstimation }) {
  const client = useMemo(() => fourni ?? new ClientEstimation(), [fourni]);
  const [jeton] = useState(() => jetonDuFragment(window.location.hash));
  const [etat, setEtat] = useState<'attente' | 'envoi' | 'transmis'>('attente');
  const [erreur, setErreur] = useState<string | null>(null);
  const [confidentialite, setConfidentialite] = useState<string | undefined>();

  useEffect(() => {
    document.title = 'Confirmer ma demande de contact — Ekera';
    window.history.replaceState(null, '', `${window.location.pathname}#/estimation/contact`);
    let actif = true;
    client
      .questionnaire()
      .then((q) => actif && setConfidentialite(q.lien_confidentialite))
      .catch(() => undefined); // le lien de la politique manque alors en pied de page ; la confirmation reste possible
    return () => {
      actif = false;
    };
  }, [client]);

  async function confirmer() {
    if (!jeton) return;
    setErreur(null);
    setEtat('envoi');
    try {
      await client.confirmer(jeton);
      setEtat('transmis');
    } catch (e) {
      setErreur(messageErreur(e));
      setEtat('attente');
    }
  }

  return (
    <Cadre confidentialite={confidentialite}>
      <section className="estim-tete" aria-labelledby="titre-confirmation">
        <h1 id="titre-confirmation">Confirmer ma demande de contact</h1>
        {!jeton && (
          <p role="alert" className="estim-alerte">
            Ce lien de confirmation n’est pas valable. Refaites une estimation pour en recevoir un nouveau.
          </p>
        )}
        {jeton && etat !== 'transmis' && (
          <>
            <p className="estim-chapo">
              Vous avez demandé, après une estimation, à être recontacté par Ekera au sujet du diagnostic complet. En
              confirmant, votre adresse et la date de la demande sont transmises à l’équipe d’Ekera, qui les garde
              pour vous recontacter. Si vous n’avez rien demandé, fermez simplement cette page : rien ne sera transmis.
            </p>
            {erreur && (
              <p role="alert" className="estim-alerte">
                {erreur}
              </p>
            )}
            <button
              type="button"
              className="btn principal estim-gros"
              disabled={etat === 'envoi'}
              onClick={() => void confirmer()}
            >
              {etat === 'envoi' ? 'Confirmation…' : 'Confirmer ma demande de contact'}
            </button>
          </>
        )}
        {etat === 'transmis' && (
          <p role="status" className="estim-ok">
            Demande transmise : l’équipe d’Ekera vous recontactera à cette adresse. Ce lien ne sert plus.
          </p>
        )}
      </section>
    </Cadre>
  );
}
